// Connectivity state for the whole app: the global offline banner, every write button that
// disables itself offline, and lead scanning all read this through useOnline().
//
// The source of truth is what actually happens on the wire, NOT navigator.onLine. That flag
// is only a hint, and a hint that has been observed lying on production: Chrome reported
// navigator.onLine === false browser-wide (every origin, no service worker involved) while
// every request from the same page returned 200. The old plugin seeded its ref straight from
// the flag and then only ever changed it from the online/offline events, so a wrong value at
// boot pinned the app in offline mode for the entire session: no event ever fires to correct
// a flag that was already stuck, and a reload re-read the same wrong flag.
//
// So instead:
//   - any HTTP response at all marks us online (a response proves the connection works)
//   - a request that fails with no response only *suspects* offline, and the suspicion is
//     confirmed by an explicit probe before the app changes state
//   - navigator.onLine never sets the state directly; it only triggers that same probe

// Liveness endpoint on the API host, registered without a middleware group in wad-api's
// RouteServiceProvider, so it touches neither the session, Redis nor the database.
const PROBE_PATH = '/up';
const PROBE_TIMEOUT_MS = 6_000;
// Floor between probes. A burst of failing requests should cost one probe, not one each.
const PROBE_MIN_INTERVAL_MS = 5_000;

// Client-only probe bookkeeping. Deliberately outside the composable so every caller shares
// it, and never touched on the server (verifyConnectivity returns before reaching it).
let probeInFlight: Promise<boolean> | null = null;
let lastProbeAt = 0;

export const useConnectivity = () => {
  // useState, not a module-level ref: SSR must never share this between requests. Starts
  // optimistic, so the app never claims to be offline until something has actually failed.
  const isOnline = useState<boolean>('wad-connectivity-online', () => true);
  const config = useRuntimeConfig();

  const markOnline = () => {
    isOnline.value = true;
  };

  const markOffline = () => {
    isOnline.value = false;
  };

  // One request to the API liveness endpoint (/up: no middleware, no DB, 2-byte body).
  //
  // Probing the API rather than our own origin is deliberate. If the CDN is up but the API is
  // unreachable, the app must behave as offline and queue writes instead of firing them at
  // something that cannot answer, so "can I reach the API" is the question worth asking.
  //
  // mode 'no-cors' keeps this free of CORS preflight and of any dependency on the endpoint's
  // CORS headers: the response is opaque, which is all we need, because a resolved fetch means
  // the request reached the network and a rejected one means it did not. 'no-store' keeps the
  // HTTP cache from answering it, and /up matches no service worker route so the SW cannot
  // answer it either. Both would otherwise report "online" while genuinely offline.
  const probe = async (): Promise<boolean> => {
    // AbortController rather than AbortSignal.timeout: on a browser without the latter the
    // call would throw inside this try and be caught as "unreachable", which would put the
    // app permanently offline. That failure mode is worse than the three extra lines.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    try {
      await fetch(`${new URL(config.public.apiBase as string).origin}${PROBE_PATH}`, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-store',
        credentials: 'omit',
        signal: controller.signal,
      });
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  };

  // Confirm or clear a suspected loss of connectivity. Safe to call on every failed request:
  // concurrent callers share one in-flight probe, and repeats inside PROBE_MIN_INTERVAL_MS
  // reuse the last answer, so a page that fails ten requests at once still sends one probe.
  const verifyConnectivity = async (): Promise<boolean> => {
    if(!import.meta.client) {
      return true;
    }
    if(probeInFlight) {
      return probeInFlight;
    }
    if(Date.now() - lastProbeAt < PROBE_MIN_INTERVAL_MS) {
      return isOnline.value;
    }

    probeInFlight = probe()
      .then((reachable) => {
        lastProbeAt = Date.now();
        if(reachable) {
          markOnline();
        } else {
          markOffline();
        }
        return reachable;
      })
      .finally(() => {
        probeInFlight = null;
      });

    return probeInFlight;
  };

  return { isOnline, markOnline, markOffline, verifyConnectivity };
};
